import {
    ArrowRight,
    Check,
    ChevronRight,
    Copy,
    Heart,
    Search,
    Share2,
    ShieldCheck,
    ShoppingBag,
    ShoppingCart,
    Sparkles,
    Star,
    Truck,
} from "lucide-react";
import {
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";
import {
    useLocation,
    useNavigate,
    useParams,
} from "react-router-dom";
import {
    getBrandStore,
} from "./SellerBrandData";
import {
    BrandStoreData,
    BrandStoreProduct,
    BrandStoreProductTag,
} from "./SellerBrandTypes";
import "./SellerBrandPage.css";
import { useCart } from "../../../context/CartContext";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";

type ProductTab =
    | "all"
    | "deal"
    | "new"
    | "bestseller";

const tabs: {
    id: ProductTab;
    label: string;
}[] = [
        {
            id: "all",
            label: "All Products",
        },
        {
            id: "deal",
            label: "All Deals",
        },
        {
            id: "new",
            label: "New Arrivals",
        },
        {
            id: "bestseller",
            label: "Best Sellers",
        },
    ];

const formatPrice = (
    value: number
) =>
    new Intl.NumberFormat(
        "en-IN"
    ).format(value);

const SellerBrandPage = () => {
    const navigate =
        useNavigate();

    const location =
        useLocation();

    const navState = location.state as {
        brandName?: string;
        brandSlug?: string;
        brand?: any;
    } | null;

    const {
        brandSlug = "salar",
    } = useParams();

    const {
        cartItems,
        addToCart,
        isProductInCart,
    } = useCart();

    const cartCount =
        cartItems.reduce(
            (
                total,
                cartItem
            ) =>
                total +
                cartItem.quantity,
            0
        );

    const [
        browsingHistoryOpen,
        setBrowsingHistoryOpen,
    ] =
        useState(false);

    const [
        followed,
        setFollowed,
    ] =
        useState(false);

    const [
        wishlist,
        setWishlist,
    ] =
        useState<(number | string)[]>([]);

    const [
        activeTab,
        setActiveTab,
    ] =
        useState<ProductTab>(
            "all"
        );

    const [
        searchQuery,
        setSearchQuery,
    ] =
        useState("");

    const [
        shareCopied,
        setShareCopied,
    ] =
        useState(false);

    const [
        store,
        setStore,
    ] =
        useState<BrandStoreData>(() => {
            const fallback =
                getBrandStore(
                    brandSlug
                );
            if (
                navState?.brandName &&
                navState.brandName !==
                    "-"
            ) {
                return {
                    ...fallback,
                    name: navState.brandName,
                    heroEyebrow: `${navState.brandName.toUpperCase()} ORIGINALS`,
                    heroTitle: `Modern essentials from ${navState.brandName}`,
                    verifiedText: `Verified ${navState.brandName} Brand`,
                };
            }
            return fallback;
        });

    const [
        isLoading,
        setIsLoading,
    ] =
        useState<boolean>(true);

    useEffect(() => {
        let isMounted = true;
        setIsLoading(true);

        const fetchBrandStore = async () => {
            const apiBase = [
                process.env.REACT_APP_API_BASE_URL,
                process.env.REACT_APP_ECOMMERCE_PERFIX,
            ]
                .filter(Boolean)
                .join("") || "https://devapi.salar.in/api/v1/product";

            try {
                const response = await axios.get(
                    `${apiBase}/e-commerce/catalog/brand-store/${encodeURIComponent(
                        brandSlug
                    )}`
                );

                if (
                    response.data &&
                    response.data.data &&
                    isMounted
                ) {
                    const data =
                        response.data.data;
                    const fallback =
                        getBrandStore(
                            brandSlug
                        );

                    setStore({
                        slug:
                            data.slug ||
                            brandSlug,
                        name:
                            data.name ||
                            fallback.name,
                        logo:
                            data.logo ||
                            fallback.logo,
                        heroImage:
                            data.heroImage ||
                            fallback.heroImage,
                        heroEyebrow:
                            data.heroEyebrow ||
                            fallback.heroEyebrow,
                        heroTitle:
                            data.heroTitle ||
                            fallback.heroTitle,
                        heroDescription:
                            data.heroDescription ||
                            fallback.heroDescription,
                        accentText:
                            data.accentText ||
                            fallback.accentText,
                        followerText:
                            data.followerText ||
                            fallback.followerText,
                        verifiedText:
                            data.verifiedText ||
                            fallback.verifiedText,
                        foundedText:
                            data.foundedText ||
                            fallback.foundedText,
                        collections:
                            data.collections &&
                            data.collections.length > 0
                                ? data.collections
                                : fallback.collections,
                        products:
                            data.products &&
                            data.products.length > 0
                                ? data.products
                                : fallback.products,
                        storyCards:
                            data.storyCards &&
                            data.storyCards.length > 0
                                ? data.storyCards
                                : fallback.storyCards,
                    });
                }
            } catch {
                if (isMounted) {
                    setStore(
                        getBrandStore(
                            brandSlug
                        )
                    );
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchBrandStore();

        return () => {
            isMounted = false;
        };
    }, [brandSlug]);

    const filteredProducts =
        useMemo(
            () => {
                const normalizedQuery =
                    searchQuery
                        .trim()
                        .toLowerCase();

                return store.products.filter(
                    (
                        product
                    ) => {
                        const tabMatch =
                            activeTab ===
                            "all" ||
                            product.tags.includes(
                                activeTab as BrandStoreProductTag
                            );

                        const searchMatch =
                            !normalizedQuery ||
                            product.title
                                .toLowerCase()
                                .includes(
                                    normalizedQuery
                                ) ||
                            product.description
                                .toLowerCase()
                                .includes(
                                    normalizedQuery
                                ) ||
                            product.category
                                .toLowerCase()
                                .includes(
                                    normalizedQuery
                                );

                        return (
                            tabMatch &&
                            searchMatch
                        );
                    }
                );
            },
            [
                activeTab,
                searchQuery,
                store.products,
            ]
        );

    const handleAddToCart = (
        product: BrandStoreProduct
    ) => {
        if (
            isProductInCart(
                product.sourceId
            )
        ) {
            return;
        }

        addToCart({
            productId:
                product.sourceId,
            title:
                product.title,
            description:
                product.description,
            image:
                product.image,
            price:
                product.price,
            mrp:
                product.mrp,
            color:
                product.color,
        });
    };

    const scrollToSection = (
        id: string
    ) => {
        document
            .getElementById(
                id
            )
            ?.scrollIntoView({
                behavior:
                    "smooth",
                block:
                    "start",
            });
    };

    const handleShare =
        async () => {
            const shareData = {
                title:
                    `${store.name} Store`,
                text:
                    `Explore the ${store.name} brand store on Salar.`,
                url:
                    window.location.href,
            };

            try {
                if (
                    navigator.share
                ) {
                    await navigator.share(
                        shareData
                    );

                    return;
                }

                await navigator.clipboard.writeText(
                    window.location.href
                );

                setShareCopied(
                    true
                );

                window.setTimeout(
                    () =>
                        setShareCopied(
                            false
                        ),
                    1600
                );
            } catch {
                setShareCopied(
                    false
                );
            }
        };

    return (
        <>
            <div className="seller-brand-desktop-navbar">
                <HomeNavbar
                    variant="detail"
                />
            </div>

            <div className="seller-brand-mobile-navbar">
                <MobileDetailNavbar
                    cartCount={
                        cartCount
                    }
                    searchPlaceholder={`Search ${store.name}`}
                    fallbackPath="/HomePage"
                />
            </div>

            <div className="seller-brand-desktop-secondary-navbar">
                <HomeSecondaryNavbar
                    variant="detail"
                    browsingHistoryOpen={
                        browsingHistoryOpen
                    }
                    onBrowsingHistoryClick={() =>
                        setBrowsingHistoryOpen(
                            (
                                previous
                            ) =>
                                !previous
                        )
                    }
                />
            </div>

            <main className="seller-brand-page">
                <div className="seller-brand-shell">
                    <div className="seller-brand-breadcrumb">
                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    "/HomePage"
                                )
                            }
                        >
                            Home
                        </button>

                        <ChevronRight
                            size={13}
                        />

                        <span>
                            Brand Store
                        </span>

                        <ChevronRight
                            size={13}
                        />

                        <strong>
                            {
                                store.name
                            }
                        </strong>
                    </div>

                    <section className="seller-brand-hero">
                        <img
                            src={
                                store.heroImage
                            }
                            alt={
                                store.name
                            }
                            className="seller-brand-hero-image"
                        />

                        <div className="seller-brand-hero-shade" />

                        <div className="seller-brand-identity-card">
                            <div className="seller-brand-logo-wrap">
                                <img
                                    src={
                                        store.logo
                                    }
                                    alt={`${store.name} logo`}
                                />
                            </div>

                            <div className="seller-brand-identity-copy">
                                <div className="seller-brand-verified-line">
                                    <span>
                                        {
                                            store.heroEyebrow
                                        }
                                    </span>

                                    <small>
                                        <ShieldCheck
                                            size={15}
                                        />

                                        {
                                            store.verifiedText
                                        }
                                    </small>
                                </div>

                                <h1>
                                    {
                                        store.heroTitle
                                    }
                                </h1>

                                <p>
                                    {
                                        store.heroDescription
                                    }
                                </p>

                                <div className="seller-brand-hero-actions">
                                    <button
                                        type="button"
                                        className="seller-brand-primary-action"
                                        onClick={() =>
                                            scrollToSection(
                                                "brand-products"
                                            )
                                        }
                                    >
                                        Shop collection

                                        <ArrowRight
                                            size={17}
                                        />
                                    </button>

                                    <button
                                        type="button"
                                        className={`seller-brand-follow-action ${followed
                                            ? "active"
                                            : ""
                                            }`}
                                        onClick={() =>
                                            setFollowed(
                                                (
                                                    previous
                                                ) =>
                                                    !previous
                                            )
                                        }
                                    >
                                        {followed ? (
                                            <>
                                                <Check
                                                    size={16}
                                                />

                                                Following
                                            </>
                                        ) : (
                                            <>
                                                <Heart
                                                    size={16}
                                                />

                                                Follow
                                            </>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        className="seller-brand-share-action"
                                        onClick={
                                            handleShare
                                        }
                                    >
                                        {shareCopied ? (
                                            <>
                                                <Copy
                                                    size={16}
                                                />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <Share2
                                                    size={16}
                                                />
                                                Share
                                            </>
                                        )}
                                    </button>
                                </div>

                                <div className="seller-brand-hero-meta">
                                    <span>
                                        {
                                            store.followerText
                                        }
                                    </span>

                                    <span>
                                        {
                                            store.foundedText
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="seller-brand-hero-note">
                            <Sparkles
                                size={16}
                            />

                            <span>
                                {
                                    store.accentText
                                }
                            </span>
                        </div>
                    </section>

                    <nav className="seller-brand-store-nav">
                        <button
                            type="button"
                            onClick={() =>
                                scrollToSection(
                                    "brand-overview"
                                )
                            }
                        >
                            Overview
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                scrollToSection(
                                    "brand-collections"
                                )
                            }
                        >
                            Collections
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setActiveTab(
                                    "deal"
                                );

                                scrollToSection(
                                    "brand-products"
                                );
                            }}
                        >
                            All Deals
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setActiveTab(
                                    "new"
                                );

                                scrollToSection(
                                    "brand-products"
                                );
                            }}
                        >
                            New Arrivals
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setActiveTab(
                                    "bestseller"
                                );

                                scrollToSection(
                                    "brand-products"
                                );
                            }}
                        >
                            Best Sellers
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                scrollToSection(
                                    "brand-story"
                                )
                            }
                        >
                            Our Story
                        </button>
                    </nav>

                    <section
                        className="seller-brand-intro"
                        id="brand-overview"
                    >
                        <div>
                            <span>
                                SHOP THE BRAND
                            </span>

                            <h2>
                                Explore {
                                    store.name
                                }
                            </h2>

                            <p>
                                Discover signature categories, seasonal edits, customer favourites and new product drops in one curated brand destination.
                            </p>
                        </div>

                        <div className="seller-brand-trust-row">
                            <span>
                                <ShieldCheck
                                    size={19}
                                />

                                Verified brand
                            </span>

                            <span>
                                <Truck
                                    size={19}
                                />

                                Fast fulfilment
                            </span>

                            <span>
                                <ShoppingBag
                                    size={19}
                                />

                                Easy shopping
                            </span>
                        </div>
                    </section>

                    <section
                        className="seller-brand-collections"
                        id="brand-collections"
                    >
                        {store.collections.map(
                            (
                                collection,
                                index
                            ) => (
                                <button
                                    type="button"
                                    className={`seller-brand-collection-card seller-brand-collection-card-${index +
                                        1
                                        }`}
                                    key={
                                        collection.id
                                    }
                                    onClick={() =>
                                        navigate(
                                            collection.route
                                        )
                                    }
                                >
                                    <img
                                        src={
                                            collection.image
                                        }
                                        alt={
                                            collection.title
                                        }
                                    />

                                    <span className="seller-brand-collection-overlay" />

                                    <span className="seller-brand-collection-copy">
                                        <small>
                                            {
                                                collection.subtitle
                                            }
                                        </small>

                                        <strong>
                                            {
                                                collection.title
                                            }
                                        </strong>

                                        <em>
                                            Explore collection

                                            <ArrowRight
                                                size={15}
                                            />
                                        </em>
                                    </span>
                                </button>
                            )
                        )}
                    </section>

                    <section
                        className="seller-brand-products"
                        id="brand-products"
                    >
                        <div className="seller-brand-section-heading">
                            <div>
                                <span>
                                    SHOP {
                                        store.name
                                    }
                                </span>

                                <h2>
                                    Discover the collection
                                </h2>

                                <p>
                                    Browse customer favourites, new arrivals and limited-time offers from the brand.
                                </p>
                            </div>

                            <div className="seller-brand-product-count">
                                <strong>
                                    {
                                        filteredProducts.length
                                    }
                                </strong>

                                <span>
                                    products
                                </span>
                            </div>
                        </div>

                        <div className="seller-brand-product-toolbar">
                            <div className="seller-brand-product-tabs">
                                {tabs.map(
                                    (
                                        tab
                                    ) => (
                                        <button
                                            type="button"
                                            key={
                                                tab.id
                                            }
                                            className={
                                                activeTab ===
                                                    tab.id
                                                    ? "active"
                                                    : ""
                                            }
                                            onClick={() =>
                                                setActiveTab(
                                                    tab.id
                                                )
                                            }
                                        >
                                            {
                                                tab.label
                                            }
                                        </button>
                                    )
                                )}
                            </div>

                            <label className="seller-brand-search">
                                <Search
                                    size={17}
                                />

                                <input
                                    type="search"
                                    value={
                                        searchQuery
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setSearchQuery(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    placeholder={`Search ${store.name} products`}
                                />
                            </label>
                        </div>

                        {filteredProducts.length >
                            0 ? (
                            <div className="seller-brand-product-grid">
                                {filteredProducts.map(
                                    (
                                        product
                                    ) => {
                                        const discount =
                                            Math.round(
                                                (
                                                    (
                                                        product.mrp -
                                                        product.price
                                                    ) /
                                                    product.mrp
                                                ) *
                                                100
                                            );

                                        const added =
                                            isProductInCart(
                                                product.sourceId
                                            );

                                        const wishlisted =
                                            wishlist.includes(
                                                product.id
                                            );

                                        return (
                                            <article
                                                className="seller-brand-product-card"
                                                key={
                                                    product.id
                                                }
                                                role="button"
                                                tabIndex={0}
                                                onClick={() =>
                                                    navigate(
                                                        `/product/${product.sourceId}`,
                                                        {
                                                            state: {
                                                                product: {
                                                                    ...product,
                                                                    id:
                                                                        product.sourceId,
                                                                    productId:
                                                                        product.sourceId,
                                                                    sourceId:
                                                                        product.sourceId,
                                                                    brand:
                                                                        store.name,
                                                                    category:
                                                                        product.category,
                                                                    breadcrumb: [
                                                                        "Home",
                                                                        store.name,
                                                                        product.category,
                                                                        product.title,
                                                                    ],
                                                                },
                                                            },
                                                        }
                                                    )
                                                }
                                                onKeyDown={(
                                                    event
                                                ) => {
                                                    if (
                                                        event.key ===
                                                        "Enter" ||
                                                        event.key ===
                                                        " "
                                                    ) {
                                                        event.preventDefault();

                                                        navigate(
                                                            `/product/${product.sourceId}`,
                                                            {
                                                                state: {
                                                                    product: {
                                                                        ...product,
                                                                        id:
                                                                            product.sourceId,
                                                                        productId:
                                                                            product.sourceId,
                                                                        sourceId:
                                                                            product.sourceId,
                                                                        brand:
                                                                            store.name,
                                                                        category:
                                                                            product.category,
                                                                        breadcrumb: [
                                                                            "Home",
                                                                            store.name,
                                                                            product.category,
                                                                            product.title,
                                                                        ],
                                                                    },
                                                                },
                                                            }
                                                        );
                                                    }
                                                }}
                                            >
                                                <div className="seller-brand-product-image-wrap">
                                                    {product.badge && (
                                                        <span className="seller-brand-product-badge">
                                                            {
                                                                product.badge
                                                            }
                                                        </span>
                                                    )}

                                                    <button
                                                        type="button"
                                                        className={`seller-brand-wishlist ${wishlisted
                                                            ? "active"
                                                            : ""
                                                            }`}
                                                        onClick={(
                                                            event
                                                        ) => {
                                                            event.stopPropagation();

                                                            setWishlist(
                                                                (
                                                                    previous
                                                                ) =>
                                                                    previous.includes(
                                                                        product.id
                                                                    )
                                                                        ? previous.filter(
                                                                            (
                                                                                id
                                                                            ) =>
                                                                                id !==
                                                                                product.id
                                                                        )
                                                                        : [
                                                                            ...previous,
                                                                            product.id,
                                                                        ]
                                                            );
                                                        }}
                                                    >
                                                        <Heart
                                                            size={18}
                                                            fill={
                                                                wishlisted
                                                                    ? "currentColor"
                                                                    : "none"
                                                            }
                                                        />
                                                    </button>

                                                    <img
                                                        src={
                                                            product.image
                                                        }
                                                        alt={
                                                            product.title
                                                        }
                                                    />
                                                </div>

                                                <div className="seller-brand-product-body">
                                                    <span className="seller-brand-product-category">
                                                        {
                                                            product.category
                                                        }
                                                    </span>

                                                    <h3>
                                                        {
                                                            product.title
                                                        }
                                                    </h3>

                                                    <p>
                                                        {
                                                            product.description
                                                        }
                                                    </p>

                                                    <div className="seller-brand-product-rating">
                                                        <strong>
                                                            {
                                                                product.rating
                                                            }
                                                        </strong>

                                                        <Star
                                                            size={14}
                                                            fill="currentColor"
                                                        />

                                                        <span>
                                                            (
                                                            {
                                                                product.reviews
                                                            }
                                                            )
                                                        </span>
                                                    </div>

                                                    <div className="seller-brand-product-price">
                                                        <strong>
                                                            ₹
                                                            {formatPrice(
                                                                product.price
                                                            )}
                                                        </strong>

                                                        <del>
                                                            ₹
                                                            {formatPrice(
                                                                product.mrp
                                                            )}
                                                        </del>

                                                        <span>
                                                            {
                                                                discount
                                                            }
                                                            % off
                                                        </span>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className={`seller-brand-cart-button ${added
                                                            ? "added"
                                                            : ""
                                                            }`}
                                                        onClick={(
                                                            event
                                                        ) => {
                                                            event.stopPropagation();

                                                            handleAddToCart(
                                                                product
                                                            );
                                                        }}
                                                    >
                                                        {added ? (
                                                            <>
                                                                <Check
                                                                    size={17}
                                                                />

                                                                Added to cart
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ShoppingCart
                                                                    size={17}
                                                                />

                                                                Add to cart
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </article>
                                        );
                                    }
                                )}
                            </div>
                        ) : (
                            <div className="seller-brand-empty-products">
                                <Search
                                    size={28}
                                />

                                <h3>
                                    No products found
                                </h3>

                                <p>
                                    Try another search or switch to a different collection tab.
                                </p>
                            </div>
                        )}
                    </section>

                    <section
                        className="seller-brand-story"
                        id="brand-story"
                    >
                        <div className="seller-brand-story-heading">
                            <span>
                                INSIDE THE BRAND
                            </span>

                            <h2>
                                The SALAR story
                            </h2>

                            <p>
                                A closer look at how the brand thinks about design, comfort, craft and the experience of building a modern everyday wardrobe.
                            </p>
                        </div>

                        {store.storyCards.map(
                            (
                                card,
                                index
                            ) => (
                                <article
                                    className={`seller-brand-story-card ${index %
                                        2 ===
                                        1
                                        ? "reverse"
                                        : ""
                                        }`}
                                    key={
                                        card.id
                                    }
                                >
                                    <div className="seller-brand-story-image">
                                        <img
                                            src={
                                                card.image
                                            }
                                            alt={
                                                card.title
                                            }
                                        />

                                        {card.stat && (
                                            <div className="seller-brand-story-stat">
                                                <strong>
                                                    {
                                                        card.stat
                                                    }
                                                </strong>

                                                <span>
                                                    {
                                                        card.statLabel
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="seller-brand-story-copy">
                                        <span>
                                            {
                                                card.eyebrow
                                            }
                                        </span>

                                        <h2>
                                            {
                                                card.title
                                            }
                                        </h2>

                                        <p>
                                            {
                                                card.description
                                            }
                                        </p>

                                        {card.secondaryDescription && (
                                            <p>
                                                {
                                                    card.secondaryDescription
                                                }
                                            </p>
                                        )}
                                    </div>
                                </article>
                            )
                        )}
                    </section>
                </div>
            </main>
        </>
    );
};

export default SellerBrandPage;
